import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import WebsiteHeader from './WebsiteHeader';
import WebsiteFooter from './WebsiteFooter';
import LandlordPrivateShell from './landlord/LandlordPrivateShell';
import { useAuth } from '../contexts/AuthContext';
import InteractiveComprehensiveTutorial from './InteractiveComprehensiveTutorial';
import InteractiveComprehensiveLandlordTutorial from './InteractiveComprehensiveLandlordTutorial';

export default function WebsiteLayout({ children }) {
  const { userType } = useAuth();
  const location = useLocation();
  const [dashboardReveal, setDashboardReveal] = React.useState(null);
  const [forceTutorial, setForceTutorial] = React.useState(false);
  const [forceLandlordTutorial, setForceLandlordTutorial] = React.useState(false);
  const isPrivateLandlordRoute = location.pathname.startsWith('/landlord') && !location.pathname.startsWith('/landlord/agency');
  const isAgencyRoute = location.pathname.startsWith('/landlord/agency');
  const isBillingPage = location.pathname === '/landlord/billing' || location.pathname === '/landlord/agency/billing';
  const isRequiredBillingFlow = isBillingPage && new URLSearchParams(location.search).get('required') === '1';

  // Pages where the private landlord sidebar should NOT be shown (full-page views)
  const noSidebarPaths = [
    '/landlord/add-property',
    '/landlord/edit-property',
    '/landlord/property/',
    '/landlord/crm/preview',
    '/landlord/immo/',
    '/landlord/onboarding',
    '/landlord-type'
  ];
  const showPrivateLandlordShell =
    isPrivateLandlordRoute &&
    userType === 'landlord' &&
    !isRequiredBillingFlow &&
    !noSidebarPaths.some(p => location.pathname.startsWith(p) || location.pathname.includes(p));
  
  // Pages where tutorial should be disabled (e.g., forms that need full interaction)
  const tutorialDisabledPages = [
    '/landlord/add-property',
    '/landlord/simple-add-property',
    '/landlord/edit-property',
    'add-property',
    'simple-add-property',
    'edit-property',
  ];
  
  const isTutorialDisabled = tutorialDisabledPages.some(path => 
    location.pathname.includes(path)
  );
  
  // Listen for custom event from dashboard tutorial button
  React.useEffect(() => {
    const handler = () => setForceLandlordTutorial(true);
    window.addEventListener('start-landlord-tutorial', handler);
    return () => window.removeEventListener('start-landlord-tutorial', handler);
  }, []);

  React.useEffect(() => {
    const revealState = location.state?.dashboardReveal;
    if (!revealState) return undefined;

    const welcomeText = location.state?.welcomeText || 'Welkom';
    setDashboardReveal(welcomeText);

    // Fallback cleanup in case animationend doesn't fire in some browsers.
    const cleanupTimer = setTimeout(() => {
      setDashboardReveal(null);
    }, 2200);

    return () => {
      clearTimeout(cleanupTimer);
    };
  }, [location.key]);
  
  return (
    <div className={`min-h-screen flex flex-col relative overflow-hidden ${
      showPrivateLandlordShell || isAgencyRoute
        ? 'bg-[#F6FAFF]'
        : isPrivateLandlordRoute
        ? 'bg-[var(--landlord-bg)]'
        : 'bg-[var(--market-bg)]'
    }`}>
      {!isAgencyRoute && !showPrivateLandlordShell && (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[linear-gradient(180deg,rgba(94,171,235,0.12)_0%,rgba(94,171,235,0.05)_45%,transparent_100%)]" />
          <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(94,171,235,0.16)_0%,rgba(94,171,235,0.06)_40%,transparent_72%)]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.018] [background-image:radial-gradient(#0f172a_0.7px,transparent_0.7px)] [background-size:7px_7px]" />
        </>
      )}
      <WebsiteHeader />
      
      {/* Main Content */}
      <main className="flex-1 relative z-10">
        {showPrivateLandlordShell ? (
          <LandlordPrivateShell>{children}</LandlordPrivateShell>
        ) : (
          children
        )}
      </main>
      
      {!isRequiredBillingFlow && !isAgencyRoute && !showPrivateLandlordShell && userType !== 'tenant' && <WebsiteFooter />}

      {/* Tenant contact strip */}
      {userType === 'tenant' && !isRequiredBillingFlow && !isAgencyRoute && !showPrivateLandlordShell && (
        <div className="relative z-10 border-t border-slate-200/80 bg-gradient-to-b from-white to-[#F6FAFF]">
          <div className="container-professional py-5 flex items-center justify-center gap-3 flex-wrap">
            <span className="text-slate-400 text-xs">© {new Date().getFullYear()} StudyStay</span>
            <span className="text-slate-300 text-xs">·</span>
            <Link to="/contact" className="text-slate-400 hover:text-[#2B6FA6] text-xs transition-colors duration-200">Contact</Link>
            <span className="text-slate-300 text-xs">·</span>
            <Link to="/legal" className="text-slate-400 hover:text-[#2B6FA6] text-xs transition-colors duration-200">Juridische documenten</Link>
          </div>
        </div>
      )}

      {dashboardReveal && (
        <div
          className="fixed inset-0 z-[120] bg-white flex items-center justify-center animate-[dashboardRevealFade_1.8s_ease_forwards]"
          onAnimationEnd={() => setDashboardReveal(null)}
        >
          <div className="text-center px-6">
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
              {dashboardReveal}
            </h1>
            <p className="mt-3 text-slate-500 text-sm md:text-base">Je dashboard wordt geladen...</p>
          </div>
        </div>
      )}
      
      {/* Global Tutorial - Tenants */}
      {!isTutorialDisabled && userType === 'tenant' && (
        <InteractiveComprehensiveTutorial
          forceShow={forceTutorial}
          onForceComplete={() => setForceTutorial(false)}
        />
      )}

      {/* Global Tutorial - Private Landlords */}
      {!isTutorialDisabled && userType === 'landlord' && isPrivateLandlordRoute && (
        <InteractiveComprehensiveLandlordTutorial
          forceShow={forceLandlordTutorial}
          onForceComplete={() => setForceLandlordTutorial(false)}
        />
      )}

      {/* Tutorial start button – tenants */}
      {userType === 'tenant' && !forceTutorial && !isTutorialDisabled && (
        <button
          onClick={() => setForceTutorial(true)}
          className="fixed bottom-6 right-6 z-[100] group flex items-center gap-2 bg-white/90 backdrop-blur-md text-blue-600 pl-4 pr-5 py-3 rounded-full shadow-lg border border-blue-100 hover:bg-blue-600 hover:text-white hover:shadow-xl hover:scale-105 transition-all duration-200"
          title="Tutorial starten"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span className="text-sm font-semibold">Tutorial</span>
        </button>
      )}

      {/* Tutorial start button – private landlords */}
      {userType === 'landlord' && isPrivateLandlordRoute && !forceLandlordTutorial && !isTutorialDisabled && (
        <button
          onClick={() => setForceLandlordTutorial(true)}
          className="fixed bottom-6 right-6 z-[100] group flex items-center gap-2 bg-white/90 backdrop-blur-md text-blue-600 pl-4 pr-5 py-3 rounded-full shadow-lg border border-blue-100 hover:bg-blue-600 hover:text-white hover:shadow-xl hover:scale-105 transition-all duration-200"
          title="Tutorial starten"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span className="text-sm font-semibold">Tutorial</span>
        </button>
      )}

      <style>{`
        @keyframes dashboardRevealFade {
          0% { opacity: 1; }
          65% { opacity: 1; }
          100% { opacity: 0; pointer-events: none; }
        }
      `}</style>
    </div>
  );
}
